/**
 * wizardly.ai - Smart Deck Themes & Slide Templates
 *
 * Each theme contains SMART template variants for 10 layout patterns:
 * title, section, bullets, bullets-two-col, stat-highlight, timeline, process, comparison, quote, content
 *
 * Templates are rendered client-side using Canvas API (1920x1080 fixed dimensions).
 * Pattern detection automatically selects the best template based on slide content.
 */

import { detectLayoutPattern, extractStatistics, parseTimelineItems, splitBulletsForColumns } from '../lib/patternDetector';

// Slide dimensions (16:9)
export const SLIDE_WIDTH = 1920;
export const SLIDE_HEIGHT = 1080;

// Color scheme tokens that can be overridden by brand guidelines
const defaultColorSchemes = {
  modernDark: {
    primary: '#a855f7',      // Purple accent
    secondary: '#6366f1',    // Indigo
    accent: '#f59e0b',       // Amber highlight
    background: '#1a1a2e',   // Dark blue-gray
    backgroundGradient: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    text: '#ffffff',
    textMuted: 'rgba(255, 255, 255, 0.7)',
    textSubtle: 'rgba(255, 255, 255, 0.5)',
  },
  cleanLight: {
    primary: '#3b82f6',      // Blue accent
    secondary: '#1e40af',    // Dark blue
    accent: '#10b981',       // Emerald highlight
    background: '#ffffff',
    backgroundGradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    text: '#1e293b',
    textMuted: '#475569',
    textSubtle: '#94a3b8',
  },
  boldGradient: {
    primary: '#f97316',      // Orange accent
    secondary: '#ea580c',    // Dark orange
    accent: '#fbbf24',       // Yellow highlight
    background: '#7c3aed',   // Violet
    backgroundGradient: 'linear-gradient(135deg, #7c3aed 0%, #c026d3 50%, #f97316 100%)',
    text: '#ffffff',
    textMuted: 'rgba(255, 255, 255, 0.85)',
    textSubtle: 'rgba(255, 255, 255, 0.6)',
  },
  // NEW THEMES
  midnight: {
    primary: '#38bdf8',      // Sky blue accent
    secondary: '#818cf8',    // Indigo
    accent: '#34d399',       // Emerald highlight
    background: '#0f172a',   // Very dark navy
    backgroundGradient: 'linear-gradient(160deg, #0f172a 0%, #1e293b 100%)',
    text: '#f1f5f9',
    textMuted: 'rgba(241, 245, 249, 0.75)',
    textSubtle: 'rgba(241, 245, 249, 0.5)',
  },
  warmEarth: {
    primary: '#d97706',      // Amber accent
    secondary: '#92400e',    // Dark amber
    accent: '#059669',       // Green highlight
    background: '#fef3c7',   // Warm cream
    backgroundGradient: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    text: '#451a03',
    textMuted: '#78350f',
    textSubtle: '#a16207',
  },
  oceanBreeze: {
    primary: '#0891b2',      // Cyan accent
    secondary: '#0e7490',    // Dark cyan
    accent: '#f472b6',       // Pink highlight
    background: '#ecfeff',   // Light cyan
    backgroundGradient: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 60%, #a5f3fc 100%)',
    text: '#164e63',
    textMuted: '#155e75',
    textSubtle: '#0e7490',
  },
};

// ============================================================================
// SMART TEMPLATE DEFINITIONS
// Position and size are in percentages (0-100) of slide dimensions
// ============================================================================

// Helper: creates a small uppercase tag label element (e.g., "KEY POINTS", "OVERVIEW")
const createTagLabel = (colorScheme) => ({
  id: 'tag-label',
  type: 'tag-label',
  placeholder: 'slideType',
  position: { x: 5, y: 3, width: 25, height: 3 },
  style: {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 1.3,
    fontWeight: '600',
    color: colorScheme.primary,
  },
});

const createTitleTemplate = (colorScheme) => ({
  id: 'title',
  name: 'Title Slide',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 8, y: 30, width: 84, height: 14 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 6.2,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'accent-line',
        type: 'shape',
        position: { x: 40, y: 46, width: 20, height: 0.5 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'subtitle',
        type: 'subtitle',
        placeholder: 'content',
        position: { x: 12, y: 50, width: 76, height: 12 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2.8,
          fontWeight: '400',
          color: colorScheme.textMuted,
          textAlign: 'center',
        },
      },
    ],
  },
});

const createBulletsTemplate = (colorScheme) => ({
  id: 'bullets',
  name: 'Bullet Points',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      createTagLabel(colorScheme),
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4.2,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.5 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'bullets',
        type: 'bullets',
        placeholder: 'bullet_points',
        position: { x: 5, y: 18, width: 92, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 3.2,
          fontWeight: '400',
          color: colorScheme.textMuted,
          textAlign: 'left',
          lineHeight: 1.8,
          bulletColor: colorScheme.primary,
          bulletSize: 1.6,
        },
      },
    ],
  },
});

const createContentTemplate = (colorScheme) => ({
  id: 'content',
  name: 'Content Block',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.6 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'content',
        type: 'content',
        placeholder: 'content',
        position: { x: 5, y: 18, width: 90, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 3.0,
          fontWeight: '400',
          color: colorScheme.textMuted,
          textAlign: 'left',
          lineHeight: 1.6,
        },
      },
    ],
  },
});

// Section Divider Template
const createSectionTemplate = (colorScheme) => ({
  id: 'section',
  name: 'Section Divider',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 10, y: 40, width: 80, height: 20 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4.5,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'left-accent',
        type: 'shape',
        position: { x: 0, y: 0, width: 0.5, height: 100 },
        style: {
          backgroundColor: colorScheme.primary,
        },
      },
    ],
  },
});

// Timeline Template
const createTimelineTemplate = (colorScheme) => ({
  id: 'timeline',
  name: 'Timeline',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.6 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'timeline',
        type: 'timeline',
        placeholder: 'bullet_points',
        position: { x: 5, y: 18, width: 90, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2.2,
          fontWeight: '400',
          color: colorScheme.textMuted,
          yearColor: colorScheme.primary,
          yearSize: 2.8,
          lineColor: colorScheme.primary,
          dotColor: colorScheme.primary,
        },
      },
    ],
  },
});

// Process/Steps Template
const createProcessTemplate = (colorScheme) => ({
  id: 'process',
  name: 'Process Flow',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      createTagLabel(colorScheme),
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4.2,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.5 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'process',
        type: 'process',
        placeholder: 'bullet_points',
        position: { x: 4, y: 18, width: 92, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 1.8,
          fontWeight: '400',
          color: colorScheme.textMuted,
          stepNumberColor: colorScheme.text,
          stepNumberBg: colorScheme.primary,
          stepNumberSize: 3,
          arrowColor: colorScheme.primary,
        },
      },
    ],
  },
});

// Bullets Two Column Template
const createBulletsTwoColTemplate = (colorScheme) => ({
  id: 'bullets-two-col',
  name: 'Two Column Bullets',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.6 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'bullets-left',
        type: 'bullets',
        placeholder: 'bullet_points_left',
        position: { x: 5, y: 18, width: 43, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2,
          fontWeight: '400',
          color: colorScheme.textMuted,
          textAlign: 'left',
          lineHeight: 2.0,
          bulletColor: colorScheme.primary,
          bulletSize: 1,
        },
      },
      {
        id: 'bullets-right',
        type: 'bullets',
        placeholder: 'bullet_points_right',
        position: { x: 52, y: 18, width: 43, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2,
          fontWeight: '400',
          color: colorScheme.textMuted,
          textAlign: 'left',
          lineHeight: 2.0,
          bulletColor: colorScheme.primary,
          bulletSize: 1,
        },
      },
    ],
  },
});

// Stat Highlight Template (Big Number with context)
const createStatHighlightTemplate = (colorScheme) => ({
  id: 'stat-highlight',
  name: 'Stat Highlight',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'stat',
        type: 'stat',
        placeholder: 'stat',
        position: { x: 10, y: 22, width: 80, height: 30 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 14,
          fontWeight: '800',
          color: colorScheme.primary,
          textAlign: 'center',
        },
      },
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 10, y: 52, width: 80, height: 12 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4.2,
          fontWeight: '600',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'content',
        type: 'content',
        placeholder: 'content',
        position: { x: 10, y: 66, width: 80, height: 28 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 3.0,
          fontWeight: '400',
          color: colorScheme.textMuted,
          textAlign: 'center',
          lineHeight: 1.6,
        },
      },
    ],
  },
});

// Quote/Testimonial Template
const createQuoteTemplate = (colorScheme) => ({
  id: 'quote',
  name: 'Quote',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'quote',
        type: 'quote',
        placeholder: 'content',
        position: { x: 10, y: 20, width: 80, height: 50 },
        style: {
          fontFamily: 'Georgia, serif',
          fontSize: 3,
          fontWeight: '400',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'left-accent',
        type: 'shape',
        position: { x: 5, y: 25, width: 0.5, height: 40 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'right-accent',
        type: 'shape',
        position: { x: 94.5, y: 25, width: 0.5, height: 40 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
    ],
  },
});

// NEW: Big Number/Statistic Template
const createBigNumberTemplate = (colorScheme) => ({
  id: 'bigNumber',
  name: 'Big Number',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 3,
          fontWeight: '600',
          color: colorScheme.textMuted,
          textAlign: 'center',
        },
      },
      {
        id: 'number',
        type: 'bigNumber',
        placeholder: 'number',
        position: { x: 10, y: 25, width: 80, height: 50 },
        style: {
          numberSize: 15,
          numberColor: colorScheme.primary,
          labelSize: 2.5,
          labelColor: colorScheme.textMuted,
          label: '', // Can be set dynamically
        },
      },
      {
        id: 'content',
        type: 'content',
        placeholder: 'content',
        position: { x: 15, y: 75, width: 70, height: 15 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2,
          fontWeight: '400',
          color: colorScheme.textSubtle,
          textAlign: 'center',
          lineHeight: 1.5,
        },
      },
    ],
  },
});

// NEW: Two Column Template
const createTwoColumnTemplate = (colorScheme) => ({
  id: 'twoColumn',
  name: 'Two Column',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.6 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      // Left column
      {
        id: 'left-content',
        type: 'content',
        placeholder: 'leftContent',
        position: { x: 5, y: 18, width: 42, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2,
          fontWeight: '400',
          color: colorScheme.textMuted,
          textAlign: 'left',
          lineHeight: 1.7,
        },
      },
      // Divider
      {
        id: 'divider',
        type: 'shape',
        position: { x: 49, y: 19, width: 0.3, height: 67 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 1,
        },
      },
      // Right column
      {
        id: 'right-content',
        type: 'content',
        placeholder: 'rightContent',
        position: { x: 53, y: 18, width: 42, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2,
          fontWeight: '400',
          color: colorScheme.textMuted,
          textAlign: 'left',
          lineHeight: 1.7,
        },
      },
    ],
  },
});

// NEW: Comparison Template
const createComparisonTemplate = (colorScheme) => ({
  id: 'comparison',
  name: 'Comparison',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 10 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 3.5,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'comparison',
        type: 'comparison',
        placeholder: 'comparison',
        position: { x: 5, y: 18, width: 90, height: 78 },
        style: {
          fontSize: 2,
          leftColor: colorScheme.primary,
          rightColor: colorScheme.secondary,
        },
      },
    ],
  },
});

// NEW: Image Left Template
const createImageLeftTemplate = (colorScheme) => ({
  id: 'imageLeft',
  name: 'Image Left',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'image',
        type: 'image',
        placeholder: 'image',
        position: { x: 0, y: 0, width: 45, height: 100 },
        style: {
          borderRadius: 0,
        },
      },
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 50, y: 15, width: 45, height: 15 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 3.5,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 50, y: 32, width: 10, height: 0.6 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'content',
        type: 'content',
        placeholder: 'content',
        position: { x: 50, y: 38, width: 45, height: 50 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2,
          fontWeight: '400',
          color: colorScheme.textMuted,
          textAlign: 'left',
          lineHeight: 1.7,
        },
      },
    ],
  },
});

// NEW: Image Right Template
const createImageRightTemplate = (colorScheme) => ({
  id: 'imageRight',
  name: 'Image Right',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 15, width: 45, height: 15 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 3.5,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 32, width: 10, height: 0.6 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'content',
        type: 'content',
        placeholder: 'content',
        position: { x: 5, y: 38, width: 45, height: 50 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2,
          fontWeight: '400',
          color: colorScheme.textMuted,
          textAlign: 'left',
          lineHeight: 1.7,
        },
      },
      {
        id: 'image',
        type: 'image',
        placeholder: 'image',
        position: { x: 55, y: 0, width: 45, height: 100 },
        style: {
          borderRadius: 0,
        },
      },
    ],
  },
});

// ============================================================================
// SMART LAYOUT TEMPLATES (Gamma-style)
// ============================================================================

// Icon Bullets - Bullet points with auto-selected icons instead of dots
const createIconBulletsTemplate = (colorScheme) => ({
  id: 'icon-bullets',
  name: 'Icon Bullets',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      createTagLabel(colorScheme),
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4.2,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.5 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'icon-bullets',
        type: 'icon-bullets',
        placeholder: 'bullet_points',
        position: { x: 4, y: 18, width: 92, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2.8,
          fontWeight: '500',
          color: colorScheme.text,
          textAlign: 'left',
          lineHeight: 2.4,
        },
      },
    ],
  },
});

// Icon Grid - Grid of icons with labels
const createIconGridTemplate = (colorScheme) => ({
  id: 'icon-grid',
  name: 'Icon Grid',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      createTagLabel(colorScheme),
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4.2,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.5 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'icon-grid',
        type: 'icon-grid',
        placeholder: 'bullet_points',
        position: { x: 4, y: 18, width: 92, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2.8,
          fontWeight: '500',
          color: colorScheme.text,
        },
      },
    ],
  },
});

// Cards - Content in styled card boxes
const createCardsTemplate = (colorScheme) => ({
  id: 'cards',
  name: 'Cards',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      createTagLabel(colorScheme),
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4.2,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.5 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'cards',
        type: 'cards',
        placeholder: 'bullet_points',
        position: { x: 3, y: 18, width: 94, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2.8,
          fontWeight: '500',
          color: colorScheme.text,
        },
      },
    ],
  },
});

// Funnel Diagram
const createFunnelTemplate = (colorScheme) => ({
  id: 'funnel',
  name: 'Funnel',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'funnel',
        type: 'funnel',
        placeholder: 'bullet_points',
        position: { x: 10, y: 18, width: 80, height: 78 },
        style: {},
      },
    ],
  },
});

// Pyramid Diagram
const createPyramidTemplate = (colorScheme) => ({
  id: 'pyramid',
  name: 'Pyramid',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'pyramid',
        type: 'pyramid',
        placeholder: 'bullet_points',
        position: { x: 10, y: 18, width: 80, height: 78 },
        style: {},
      },
    ],
  },
});

// Venn Diagram
const createVennTemplate = (colorScheme) => ({
  id: 'venn',
  name: 'Venn Diagram',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'venn',
        type: 'venn',
        placeholder: 'bullet_points',
        position: { x: 10, y: 18, width: 80, height: 78 },
        style: {},
      },
    ],
  },
});

// Quadrant / 2x2 Matrix
const createQuadrantTemplate = (colorScheme) => ({
  id: 'quadrant',
  name: 'Quadrant Matrix',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'quadrant',
        type: 'quadrant',
        placeholder: 'bullet_points',
        position: { x: 8, y: 18, width: 84, height: 78 },
        style: {},
      },
    ],
  },
});

// Bullseye / Target
const createBullseyeTemplate = (colorScheme) => ({
  id: 'bullseye',
  name: 'Bullseye Target',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'bullseye',
        type: 'bullseye',
        placeholder: 'bullet_points',
        position: { x: 10, y: 18, width: 80, height: 78 },
        style: {},
      },
    ],
  },
});

// Cycle Diagram
const createCycleTemplate = (colorScheme) => ({
  id: 'cycle',
  name: 'Cycle',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'cycle',
        type: 'cycle',
        placeholder: 'bullet_points',
        position: { x: 10, y: 18, width: 80, height: 78 },
        style: {},
      },
    ],
  },
});

// Staircase / Steps
const createStaircaseTemplate = (colorScheme) => ({
  id: 'staircase',
  name: 'Staircase',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.6 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'staircase',
        type: 'staircase',
        placeholder: 'bullet_points',
        position: { x: 5, y: 24, width: 90, height: 70 },
        style: {},
      },
    ],
  },
});

// Chain Diagram
const createChainTemplate = (colorScheme) => ({
  id: 'chain',
  name: 'Chain',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'chain',
        type: 'chain',
        placeholder: 'bullet_points',
        position: { x: 5, y: 24, width: 90, height: 70 },
        style: {},
      },
    ],
  },
});

// Versus / Two-panel comparison
const createVersusTemplate = (colorScheme) => ({
  id: 'versus',
  name: 'Versus',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'versus',
        type: 'versus',
        placeholder: 'bullet_points',
        position: { x: 5, y: 18, width: 90, height: 78 },
        style: {},
      },
    ],
  },
});

// Iceberg Diagram
const createIcebergTemplate = (colorScheme) => ({
  id: 'iceberg',
  name: 'Iceberg',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'iceberg',
        type: 'iceberg',
        placeholder: 'bullet_points',
        position: { x: 10, y: 18, width: 80, height: 78 },
        style: {},
      },
    ],
  },
});

const createClosingTemplate = (colorScheme) => ({
  id: 'closing',
  name: 'Closing Slide',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 10, y: 28, width: 80, height: 12 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 5,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'content',
        type: 'content',
        placeholder: 'content',
        position: { x: 15, y: 42, width: 70, height: 10 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2.8,
          fontWeight: '400',
          color: colorScheme.textMuted,
          textAlign: 'center',
          lineHeight: 1.6,
        },
      },
      {
        id: 'bullets',
        type: 'icon-bullets',
        placeholder: 'bullet_points',
        position: { x: 15, y: 55, width: 70, height: 38 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2.4,
          fontWeight: '400',
          color: colorScheme.text,
          textAlign: 'center',
          lineHeight: 1.5,
        },
      },
      {
        id: 'top-accent',
        type: 'shape',
        position: { x: 0, y: 0, width: 100, height: 1 },
        style: {
          backgroundColor: colorScheme.primary,
        },
      },
      {
        id: 'bottom-accent',
        type: 'shape',
        position: { x: 0, y: 99, width: 100, height: 1 },
        style: {
          backgroundColor: colorScheme.primary,
        },
      },
    ],
  },
});

// ============================================================================
// NEW SMART LAYOUT TEMPLATES
// ============================================================================

const createFeatureShowcaseTemplate = (colorScheme) => ({
  id: 'feature-showcase',
  name: 'Feature Showcase',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient || colorScheme.background,
    },
    elements: [
      createTagLabel(colorScheme),
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4.2,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.5 },
        style: { backgroundColor: colorScheme.primary },
      },
      {
        id: 'feature-showcase',
        type: 'feature-showcase',
        placeholder: 'bullet_points',
        position: { x: 3, y: 18, width: 94, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2.8,
          fontWeight: '500',
          color: colorScheme.text,
        },
      },
    ],
  },
});

const createNumberedListTemplate = (colorScheme) => ({
  id: 'numbered-list',
  name: 'Numbered List',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient || colorScheme.background,
    },
    elements: [
      createTagLabel(colorScheme),
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4.2,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.5 },
        style: { backgroundColor: colorScheme.primary },
      },
      {
        id: 'numbered-list',
        type: 'numbered-list',
        placeholder: 'bullet_points',
        position: { x: 4, y: 18, width: 92, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 3.0,
          fontWeight: '400',
          color: colorScheme.text,
        },
      },
    ],
  },
});

const createBigStatementTemplate = (colorScheme) => ({
  id: 'big-statement',
  name: 'Big Statement',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient || colorScheme.background,
    },
    elements: [
      {
        id: 'top-accent',
        type: 'shape',
        position: { x: 30, y: 28, width: 40, height: 0.3 },
        style: { backgroundColor: colorScheme.primary },
      },
      {
        id: 'title',
        type: 'big-statement',
        placeholder: 'title',
        position: { x: 8, y: 30, width: 84, height: 40 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 7,
          fontWeight: '800',
          color: colorScheme.text,
          textAlign: 'center',
        },
      },
      {
        id: 'bottom-accent',
        type: 'shape',
        position: { x: 30, y: 72, width: 40, height: 0.3 },
        style: { backgroundColor: colorScheme.primary },
      },
      {
        id: 'content',
        type: 'content',
        placeholder: 'content',
        position: { x: 20, y: 75, width: 60, height: 15 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2.4,
          fontWeight: '400',
          color: colorScheme.textMuted,
          textAlign: 'center',
        },
      },
    ],
  },
});

const createMetricsDashboardTemplate = (colorScheme) => ({
  id: 'metrics-dashboard',
  name: 'Metrics Dashboard',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient || colorScheme.background,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 5, width: 90, height: 14 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4.2,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.4 },
        style: { backgroundColor: colorScheme.primary },
      },
      {
        id: 'metrics-dashboard',
        type: 'metrics-dashboard',
        placeholder: 'bullet_points',
        position: { x: 4, y: 24, width: 92, height: 68 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 3.0,
          fontWeight: '500',
          color: colorScheme.text,
        },
      },
    ],
  },
});

// ============================================================================
// ADDITIONAL LAYOUT TEMPLATES
// ============================================================================

const createTextWithImageTemplate = (colorScheme) => ({
  id: 'text-with-image',
  name: 'Split Layout',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient || colorScheme.background,
    },
    elements: [
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 52, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 3.8,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.6 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'bullets',
        type: 'bullets',
        placeholder: 'bullet_points',
        position: { x: 5, y: 18, width: 52, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2.8,
          fontWeight: '400',
          color: colorScheme.textMuted,
          lineHeight: 2.0,
        },
      },
      {
        id: 'image-block',
        type: 'image-placeholder',
        placeholder: 'title',
        position: { x: 60, y: 5, width: 36, height: 90 },
        style: {
          primaryColor: colorScheme.primary,
          secondaryColor: colorScheme.secondary,
          borderRadius: 16,
        },
      },
    ],
  },
});

const createThreeColumnTemplate = (colorScheme) => ({
  id: 'three-column',
  name: 'Three Columns',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient || colorScheme.background,
    },
    elements: [
      createTagLabel(colorScheme),
      {
        id: 'title',
        type: 'title',
        placeholder: 'title',
        position: { x: 5, y: 6, width: 90, height: 8 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 4.2,
          fontWeight: '700',
          color: colorScheme.text,
          textAlign: 'left',
        },
      },
      {
        id: 'accent-bar',
        type: 'shape',
        position: { x: 5, y: 15, width: 8, height: 0.5 },
        style: {
          backgroundColor: colorScheme.primary,
          borderRadius: 2,
        },
      },
      {
        id: 'columns',
        type: 'three-column',
        placeholder: 'bullet_points',
        position: { x: 3, y: 18, width: 94, height: 78 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 2.4,
          fontWeight: '400',
          color: colorScheme.textMuted,
        },
      },
    ],
  },
});

const createQuoteCardTemplate = (colorScheme) => ({
  id: 'quote-card',
  name: 'Quote Card',
  layout: {
    background: {
      type: 'gradient',
      value: colorScheme.backgroundGradient || colorScheme.background,
    },
    elements: [
      {
        id: 'quote-frame',
        type: 'quote-card',
        placeholder: 'title',
        position: { x: 10, y: 10, width: 80, height: 80 },
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 3.5,
          fontWeight: '500',
          color: colorScheme.text,
          quoteMarkColor: colorScheme.primary,
          borderColor: colorScheme.primary,
        },
      },
    ],
  },
});

// ============================================================================
// DECK THEMES - Each theme includes all smart template variants
// ============================================================================

export const themes = [
  {
    id: 'modern-dark',
    name: 'Modern Dark',
    description: 'Sleek dark theme with purple accents',
    colorScheme: defaultColorSchemes.modernDark,
    templates: {
      title: createTitleTemplate(defaultColorSchemes.modernDark),
      section: createSectionTemplate(defaultColorSchemes.modernDark),
      bullets: createBulletsTemplate(defaultColorSchemes.modernDark),
      'bullets-two-col': createBulletsTwoColTemplate(defaultColorSchemes.modernDark),
      'stat-highlight': createStatHighlightTemplate(defaultColorSchemes.modernDark),
      timeline: createTimelineTemplate(defaultColorSchemes.modernDark),
      process: createProcessTemplate(defaultColorSchemes.modernDark),
      content: createContentTemplate(defaultColorSchemes.modernDark),
      quote: createQuoteTemplate(defaultColorSchemes.modernDark),
      bigNumber: createBigNumberTemplate(defaultColorSchemes.modernDark),
      twoColumn: createTwoColumnTemplate(defaultColorSchemes.modernDark),
      comparison: createComparisonTemplate(defaultColorSchemes.modernDark),
      imageLeft: createImageLeftTemplate(defaultColorSchemes.modernDark),
      imageRight: createImageRightTemplate(defaultColorSchemes.modernDark),
      closing: createClosingTemplate(defaultColorSchemes.modernDark),
      // Smart Layout templates (Gamma-style)
      'icon-bullets': createIconBulletsTemplate(defaultColorSchemes.modernDark),
      'icon-grid': createIconGridTemplate(defaultColorSchemes.modernDark),
      cards: createCardsTemplate(defaultColorSchemes.modernDark),
      funnel: createFunnelTemplate(defaultColorSchemes.modernDark),
      pyramid: createPyramidTemplate(defaultColorSchemes.modernDark),
      venn: createVennTemplate(defaultColorSchemes.modernDark),
      quadrant: createQuadrantTemplate(defaultColorSchemes.modernDark),
      bullseye: createBullseyeTemplate(defaultColorSchemes.modernDark),
      cycle: createCycleTemplate(defaultColorSchemes.modernDark),
      staircase: createStaircaseTemplate(defaultColorSchemes.modernDark),
      chain: createChainTemplate(defaultColorSchemes.modernDark),
      versus: createVersusTemplate(defaultColorSchemes.modernDark),
      iceberg: createIcebergTemplate(defaultColorSchemes.modernDark),
      'feature-showcase': createFeatureShowcaseTemplate(defaultColorSchemes.modernDark),
      'numbered-list': createNumberedListTemplate(defaultColorSchemes.modernDark),
      'big-statement': createBigStatementTemplate(defaultColorSchemes.modernDark),
      'metrics-dashboard': createMetricsDashboardTemplate(defaultColorSchemes.modernDark),
      'text-with-image': createTextWithImageTemplate(defaultColorSchemes.modernDark),
      'three-column': createThreeColumnTemplate(defaultColorSchemes.modernDark),
      'quote-card': createQuoteCardTemplate(defaultColorSchemes.modernDark),
    },
  },
  {
    id: 'clean-light',
    name: 'Clean Light',
    description: 'Professional light theme with blue accents',
    colorScheme: defaultColorSchemes.cleanLight,
    templates: {
      title: createTitleTemplate(defaultColorSchemes.cleanLight),
      section: createSectionTemplate(defaultColorSchemes.cleanLight),
      bullets: createBulletsTemplate(defaultColorSchemes.cleanLight),
      'bullets-two-col': createBulletsTwoColTemplate(defaultColorSchemes.cleanLight),
      'stat-highlight': createStatHighlightTemplate(defaultColorSchemes.cleanLight),
      timeline: createTimelineTemplate(defaultColorSchemes.cleanLight),
      process: createProcessTemplate(defaultColorSchemes.cleanLight),
      content: createContentTemplate(defaultColorSchemes.cleanLight),
      quote: createQuoteTemplate(defaultColorSchemes.cleanLight),
      bigNumber: createBigNumberTemplate(defaultColorSchemes.cleanLight),
      twoColumn: createTwoColumnTemplate(defaultColorSchemes.cleanLight),
      comparison: createComparisonTemplate(defaultColorSchemes.cleanLight),
      imageLeft: createImageLeftTemplate(defaultColorSchemes.cleanLight),
      imageRight: createImageRightTemplate(defaultColorSchemes.cleanLight),
      closing: createClosingTemplate(defaultColorSchemes.cleanLight),
      // Smart Layout templates (Gamma-style)
      'icon-bullets': createIconBulletsTemplate(defaultColorSchemes.cleanLight),
      'icon-grid': createIconGridTemplate(defaultColorSchemes.cleanLight),
      cards: createCardsTemplate(defaultColorSchemes.cleanLight),
      funnel: createFunnelTemplate(defaultColorSchemes.cleanLight),
      pyramid: createPyramidTemplate(defaultColorSchemes.cleanLight),
      venn: createVennTemplate(defaultColorSchemes.cleanLight),
      quadrant: createQuadrantTemplate(defaultColorSchemes.cleanLight),
      bullseye: createBullseyeTemplate(defaultColorSchemes.cleanLight),
      cycle: createCycleTemplate(defaultColorSchemes.cleanLight),
      staircase: createStaircaseTemplate(defaultColorSchemes.cleanLight),
      chain: createChainTemplate(defaultColorSchemes.cleanLight),
      versus: createVersusTemplate(defaultColorSchemes.cleanLight),
      iceberg: createIcebergTemplate(defaultColorSchemes.cleanLight),
      'feature-showcase': createFeatureShowcaseTemplate(defaultColorSchemes.cleanLight),
      'numbered-list': createNumberedListTemplate(defaultColorSchemes.cleanLight),
      'big-statement': createBigStatementTemplate(defaultColorSchemes.cleanLight),
      'metrics-dashboard': createMetricsDashboardTemplate(defaultColorSchemes.cleanLight),
      'text-with-image': createTextWithImageTemplate(defaultColorSchemes.cleanLight),
      'three-column': createThreeColumnTemplate(defaultColorSchemes.cleanLight),
      'quote-card': createQuoteCardTemplate(defaultColorSchemes.cleanLight),
    },
  },
  {
    id: 'bold-gradient',
    name: 'Bold Gradient',
    description: 'Vibrant gradient theme with orange accents',
    colorScheme: defaultColorSchemes.boldGradient,
    templates: {
      title: createTitleTemplate(defaultColorSchemes.boldGradient),
      section: createSectionTemplate(defaultColorSchemes.boldGradient),
      bullets: createBulletsTemplate(defaultColorSchemes.boldGradient),
      'bullets-two-col': createBulletsTwoColTemplate(defaultColorSchemes.boldGradient),
      'stat-highlight': createStatHighlightTemplate(defaultColorSchemes.boldGradient),
      timeline: createTimelineTemplate(defaultColorSchemes.boldGradient),
      process: createProcessTemplate(defaultColorSchemes.boldGradient),
      content: createContentTemplate(defaultColorSchemes.boldGradient),
      quote: createQuoteTemplate(defaultColorSchemes.boldGradient),
      bigNumber: createBigNumberTemplate(defaultColorSchemes.boldGradient),
      twoColumn: createTwoColumnTemplate(defaultColorSchemes.boldGradient),
      comparison: createComparisonTemplate(defaultColorSchemes.boldGradient),
      imageLeft: createImageLeftTemplate(defaultColorSchemes.boldGradient),
      imageRight: createImageRightTemplate(defaultColorSchemes.boldGradient),
      closing: createClosingTemplate(defaultColorSchemes.boldGradient),
      // Smart Layout templates (Gamma-style)
      'icon-bullets': createIconBulletsTemplate(defaultColorSchemes.boldGradient),
      'icon-grid': createIconGridTemplate(defaultColorSchemes.boldGradient),
      cards: createCardsTemplate(defaultColorSchemes.boldGradient),
      funnel: createFunnelTemplate(defaultColorSchemes.boldGradient),
      pyramid: createPyramidTemplate(defaultColorSchemes.boldGradient),
      venn: createVennTemplate(defaultColorSchemes.boldGradient),
      quadrant: createQuadrantTemplate(defaultColorSchemes.boldGradient),
      bullseye: createBullseyeTemplate(defaultColorSchemes.boldGradient),
      cycle: createCycleTemplate(defaultColorSchemes.boldGradient),
      staircase: createStaircaseTemplate(defaultColorSchemes.boldGradient),
      chain: createChainTemplate(defaultColorSchemes.boldGradient),
      versus: createVersusTemplate(defaultColorSchemes.boldGradient),
      iceberg: createIcebergTemplate(defaultColorSchemes.boldGradient),
      'feature-showcase': createFeatureShowcaseTemplate(defaultColorSchemes.boldGradient),
      'numbered-list': createNumberedListTemplate(defaultColorSchemes.boldGradient),
      'big-statement': createBigStatementTemplate(defaultColorSchemes.boldGradient),
      'metrics-dashboard': createMetricsDashboardTemplate(defaultColorSchemes.boldGradient),
      'text-with-image': createTextWithImageTemplate(defaultColorSchemes.boldGradient),
      'three-column': createThreeColumnTemplate(defaultColorSchemes.boldGradient),
      'quote-card': createQuoteCardTemplate(defaultColorSchemes.boldGradient),
    },
  },
  {
    id: 'midnight',
    name: 'Midnight',
    description: 'Deep navy dark theme with sky blue accents',
    colorScheme: defaultColorSchemes.midnight,
    templates: {
      title: createTitleTemplate(defaultColorSchemes.midnight),
      section: createSectionTemplate(defaultColorSchemes.midnight),
      bullets: createBulletsTemplate(defaultColorSchemes.midnight),
      'bullets-two-col': createBulletsTwoColTemplate(defaultColorSchemes.midnight),
      'stat-highlight': createStatHighlightTemplate(defaultColorSchemes.midnight),
      timeline: createTimelineTemplate(defaultColorSchemes.midnight),
      process: createProcessTemplate(defaultColorSchemes.midnight),
      content: createContentTemplate(defaultColorSchemes.midnight),
      quote: createQuoteTemplate(defaultColorSchemes.midnight),
      bigNumber: createBigNumberTemplate(defaultColorSchemes.midnight),
      twoColumn: createTwoColumnTemplate(defaultColorSchemes.midnight),
      comparison: createComparisonTemplate(defaultColorSchemes.midnight),
      imageLeft: createImageLeftTemplate(defaultColorSchemes.midnight),
      imageRight: createImageRightTemplate(defaultColorSchemes.midnight),
      closing: createClosingTemplate(defaultColorSchemes.midnight),
      'icon-bullets': createIconBulletsTemplate(defaultColorSchemes.midnight),
      'icon-grid': createIconGridTemplate(defaultColorSchemes.midnight),
      cards: createCardsTemplate(defaultColorSchemes.midnight),
      funnel: createFunnelTemplate(defaultColorSchemes.midnight),
      pyramid: createPyramidTemplate(defaultColorSchemes.midnight),
      venn: createVennTemplate(defaultColorSchemes.midnight),
      quadrant: createQuadrantTemplate(defaultColorSchemes.midnight),
      bullseye: createBullseyeTemplate(defaultColorSchemes.midnight),
      cycle: createCycleTemplate(defaultColorSchemes.midnight),
      staircase: createStaircaseTemplate(defaultColorSchemes.midnight),
      chain: createChainTemplate(defaultColorSchemes.midnight),
      versus: createVersusTemplate(defaultColorSchemes.midnight),
      iceberg: createIcebergTemplate(defaultColorSchemes.midnight),
      'feature-showcase': createFeatureShowcaseTemplate(defaultColorSchemes.midnight),
      'numbered-list': createNumberedListTemplate(defaultColorSchemes.midnight),
      'big-statement': createBigStatementTemplate(defaultColorSchemes.midnight),
      'metrics-dashboard': createMetricsDashboardTemplate(defaultColorSchemes.midnight),
      'text-with-image': createTextWithImageTemplate(defaultColorSchemes.midnight),
      'three-column': createThreeColumnTemplate(defaultColorSchemes.midnight),
      'quote-card': createQuoteCardTemplate(defaultColorSchemes.midnight),
    },
  },
  {
    id: 'warm-earth',
    name: 'Warm Earth',
    description: 'Warm cream tones with amber accents',
    colorScheme: defaultColorSchemes.warmEarth,
    templates: {
      title: createTitleTemplate(defaultColorSchemes.warmEarth),
      section: createSectionTemplate(defaultColorSchemes.warmEarth),
      bullets: createBulletsTemplate(defaultColorSchemes.warmEarth),
      'bullets-two-col': createBulletsTwoColTemplate(defaultColorSchemes.warmEarth),
      'stat-highlight': createStatHighlightTemplate(defaultColorSchemes.warmEarth),
      timeline: createTimelineTemplate(defaultColorSchemes.warmEarth),
      process: createProcessTemplate(defaultColorSchemes.warmEarth),
      content: createContentTemplate(defaultColorSchemes.warmEarth),
      quote: createQuoteTemplate(defaultColorSchemes.warmEarth),
      bigNumber: createBigNumberTemplate(defaultColorSchemes.warmEarth),
      twoColumn: createTwoColumnTemplate(defaultColorSchemes.warmEarth),
      comparison: createComparisonTemplate(defaultColorSchemes.warmEarth),
      imageLeft: createImageLeftTemplate(defaultColorSchemes.warmEarth),
      imageRight: createImageRightTemplate(defaultColorSchemes.warmEarth),
      closing: createClosingTemplate(defaultColorSchemes.warmEarth),
      'icon-bullets': createIconBulletsTemplate(defaultColorSchemes.warmEarth),
      'icon-grid': createIconGridTemplate(defaultColorSchemes.warmEarth),
      cards: createCardsTemplate(defaultColorSchemes.warmEarth),
      funnel: createFunnelTemplate(defaultColorSchemes.warmEarth),
      pyramid: createPyramidTemplate(defaultColorSchemes.warmEarth),
      venn: createVennTemplate(defaultColorSchemes.warmEarth),
      quadrant: createQuadrantTemplate(defaultColorSchemes.warmEarth),
      bullseye: createBullseyeTemplate(defaultColorSchemes.warmEarth),
      cycle: createCycleTemplate(defaultColorSchemes.warmEarth),
      staircase: createStaircaseTemplate(defaultColorSchemes.warmEarth),
      chain: createChainTemplate(defaultColorSchemes.warmEarth),
      versus: createVersusTemplate(defaultColorSchemes.warmEarth),
      iceberg: createIcebergTemplate(defaultColorSchemes.warmEarth),
      'feature-showcase': createFeatureShowcaseTemplate(defaultColorSchemes.warmEarth),
      'numbered-list': createNumberedListTemplate(defaultColorSchemes.warmEarth),
      'big-statement': createBigStatementTemplate(defaultColorSchemes.warmEarth),
      'metrics-dashboard': createMetricsDashboardTemplate(defaultColorSchemes.warmEarth),
      'text-with-image': createTextWithImageTemplate(defaultColorSchemes.warmEarth),
      'three-column': createThreeColumnTemplate(defaultColorSchemes.warmEarth),
      'quote-card': createQuoteCardTemplate(defaultColorSchemes.warmEarth),
    },
  },
  {
    id: 'ocean-breeze',
    name: 'Ocean Breeze',
    description: 'Fresh cyan tones with a coastal feel',
    colorScheme: defaultColorSchemes.oceanBreeze,
    templates: {
      title: createTitleTemplate(defaultColorSchemes.oceanBreeze),
      section: createSectionTemplate(defaultColorSchemes.oceanBreeze),
      bullets: createBulletsTemplate(defaultColorSchemes.oceanBreeze),
      'bullets-two-col': createBulletsTwoColTemplate(defaultColorSchemes.oceanBreeze),
      'stat-highlight': createStatHighlightTemplate(defaultColorSchemes.oceanBreeze),
      timeline: createTimelineTemplate(defaultColorSchemes.oceanBreeze),
      process: createProcessTemplate(defaultColorSchemes.oceanBreeze),
      content: createContentTemplate(defaultColorSchemes.oceanBreeze),
      quote: createQuoteTemplate(defaultColorSchemes.oceanBreeze),
      bigNumber: createBigNumberTemplate(defaultColorSchemes.oceanBreeze),
      twoColumn: createTwoColumnTemplate(defaultColorSchemes.oceanBreeze),
      comparison: createComparisonTemplate(defaultColorSchemes.oceanBreeze),
      imageLeft: createImageLeftTemplate(defaultColorSchemes.oceanBreeze),
      imageRight: createImageRightTemplate(defaultColorSchemes.oceanBreeze),
      closing: createClosingTemplate(defaultColorSchemes.oceanBreeze),
      'icon-bullets': createIconBulletsTemplate(defaultColorSchemes.oceanBreeze),
      'icon-grid': createIconGridTemplate(defaultColorSchemes.oceanBreeze),
      cards: createCardsTemplate(defaultColorSchemes.oceanBreeze),
      funnel: createFunnelTemplate(defaultColorSchemes.oceanBreeze),
      pyramid: createPyramidTemplate(defaultColorSchemes.oceanBreeze),
      venn: createVennTemplate(defaultColorSchemes.oceanBreeze),
      quadrant: createQuadrantTemplate(defaultColorSchemes.oceanBreeze),
      bullseye: createBullseyeTemplate(defaultColorSchemes.oceanBreeze),
      cycle: createCycleTemplate(defaultColorSchemes.oceanBreeze),
      staircase: createStaircaseTemplate(defaultColorSchemes.oceanBreeze),
      chain: createChainTemplate(defaultColorSchemes.oceanBreeze),
      versus: createVersusTemplate(defaultColorSchemes.oceanBreeze),
      iceberg: createIcebergTemplate(defaultColorSchemes.oceanBreeze),
      'feature-showcase': createFeatureShowcaseTemplate(defaultColorSchemes.oceanBreeze),
      'numbered-list': createNumberedListTemplate(defaultColorSchemes.oceanBreeze),
      'big-statement': createBigStatementTemplate(defaultColorSchemes.oceanBreeze),
      'metrics-dashboard': createMetricsDashboardTemplate(defaultColorSchemes.oceanBreeze),
      'text-with-image': createTextWithImageTemplate(defaultColorSchemes.oceanBreeze),
      'three-column': createThreeColumnTemplate(defaultColorSchemes.oceanBreeze),
      'quote-card': createQuoteCardTemplate(defaultColorSchemes.oceanBreeze),
    },
  },
];

// ============================================================================
// IMAGE VARIANT GENERATOR — Dynamically creates a "with-image" version of any template
// Shrinks text content to left ~55% and adds an image-placeholder on the right ~36%
// ============================================================================

// Templates that should NOT get image variants (diagrams, special layouts)
const NO_IMAGE_VARIANT = new Set([
  'title', 'closing', 'section', 'stat-highlight', 'big-statement', 'quote', 'quote-card',
  'comparison', 'funnel', 'pyramid', 'venn', 'quadrant', 'bullseye', 'cycle',
  'staircase', 'chain', 'versus', 'iceberg', 'metrics-dashboard', 'timeline', 'process',
  'text-with-image', 'imageLeft', 'imageRight', 'bullets-two-col', 'twoColumn', 'bigNumber',
]);

const imageVariantCache = new Map();

function createImageVariant(template, colorScheme) {
  if (!template || !template.layout) return template;

  const cacheKey = `${template.id}_${colorScheme.primary || ''}`;
  if (imageVariantCache.has(cacheKey)) return imageVariantCache.get(cacheKey);

  // Deep clone the template
  const variant = JSON.parse(JSON.stringify(template));
  variant.id = `${template.id}-with-image`;
  variant.name = `${template.name} + Image`;

  // Shrink all elements to fit in left ~55%
  for (const el of variant.layout.elements) {
    const pos = el.position;
    const originalWidth = pos.width;
    // Shrink width: elements wider than 55% get clamped to 52%
    if (pos.width > 55) {
      pos.width = 52;
    }
    // Ensure elements don't extend past 57% (leave room for image at 60%)
    if (pos.x + pos.width > 57) {
      pos.width = Math.max(20, 57 - pos.x);
    }
    // Scale font size proportionally for content elements that shrunk significantly
    if (originalWidth > 55 && el.style?.fontSize) {
      const scale = pos.width / originalWidth;
      el.style.fontSize = Math.round(el.style.fontSize * scale * 100) / 100;
    }
    // Add widthScale hint for renderers that use slideHeight-based font sizing
    if (originalWidth > 55) {
      el.style = el.style || {};
      el.style._widthScale = pos.width / originalWidth;
    }
  }

  // Expand the main content element (cards/icon-grid/bullets) to use more vertical space
  // In image variants, the narrower width means content needs more vertical room
  const contentTypes = new Set(['cards', 'icon-grid', 'icon-bullets', 'bullets', 'feature-showcase', 'numbered-list']);
  const mainContent = variant.layout.elements.find(el => contentTypes.has(el.type));
  if (mainContent) {
    const pos = mainContent.position;
    // Move up and expand downward to fill available space
    if (pos.y > 18) {
      const shift = Math.min(8, pos.y - 15);
      pos.y = pos.y - shift;
      pos.height = Math.min(82, pos.height + shift);
    }
  }

  // Add image-placeholder element on the right side
  variant.layout.elements.push({
    id: 'image-block',
    type: 'image-placeholder',
    placeholder: 'title',
    position: { x: 60, y: 5, width: 36, height: 90 },
    style: {
      primaryColor: colorScheme.primary,
      secondaryColor: colorScheme.secondary,
      borderRadius: 16,
    },
  });

  imageVariantCache.set(cacheKey, variant);
  return variant;
}

// ============================================================================
// SMART TEMPLATE SELECTION - Auto-detect best template for content
// Uses pattern detection for content-aware layout selection
// ============================================================================

/**
 * Get template variant based on slide content and position
 * Uses smart pattern detection to pick the best layout for the content
 */
export const getTemplateForSlide = (theme, slide, index, totalSlides) => {
  // Map pattern/type to template (with fallbacks)
  const templateMap = {
    // Core layouts
    'title': theme.templates.title,
    'section': theme.templates.section || theme.templates.title,
    'bullets': theme.templates.bullets,
    'bullets-two-col': theme.templates['bullets-two-col'] || theme.templates.bullets,
    'stat-highlight': theme.templates['stat-highlight'] || theme.templates.bigNumber || theme.templates.content,
    'timeline': theme.templates.timeline || theme.templates.bullets,
    'process': theme.templates.process || theme.templates.bullets,
    'comparison': theme.templates.comparison || theme.templates.twoColumn,
    'quote': theme.templates.quote || theme.templates.content,
    'content': theme.templates.content,
    'closing': theme.templates.closing || theme.templates.title,
    // Smart layouts (Gamma-style)
    'icon-bullets': theme.templates['icon-bullets'] || theme.templates.bullets,
    'icon-grid': theme.templates['icon-grid'] || theme.templates['icon-bullets'] || theme.templates.bullets,
    'cards': theme.templates.cards || theme.templates['icon-grid'] || theme.templates.bullets,
    // Smart diagrams
    'funnel': theme.templates.funnel || theme.templates.process,
    'pyramid': theme.templates.pyramid || theme.templates.funnel || theme.templates.bullets,
    'venn': theme.templates.venn || theme.templates.comparison || theme.templates.content,
    'quadrant': theme.templates.quadrant || theme.templates.comparison || theme.templates.content,
    'bullseye': theme.templates.bullseye || theme.templates.pyramid || theme.templates.bullets,
    'cycle': theme.templates.cycle || theme.templates.process || theme.templates.bullets,
    'staircase': theme.templates.staircase || theme.templates.process || theme.templates.bullets,
    'chain': theme.templates.chain || theme.templates.process || theme.templates.bullets,
    'versus': theme.templates.versus || theme.templates.comparison || theme.templates.content,
    'iceberg': theme.templates.iceberg || theme.templates.pyramid || theme.templates.content,
    // New smart layouts
    'feature-showcase': theme.templates['feature-showcase'] || theme.templates.cards,
    'numbered-list': theme.templates['numbered-list'] || theme.templates.bullets,
    'big-statement': theme.templates['big-statement'] || theme.templates.content,
    'metrics-dashboard': theme.templates['metrics-dashboard'] || theme.templates['stat-highlight'] || theme.templates.content,
    // Additional layouts
    'text-with-image': theme.templates['text-with-image'] || theme.templates.bullets,
    'three-column': theme.templates['three-column'] || theme.templates.cards,
    'quote-card': theme.templates['quote-card'] || theme.templates.quote || theme.templates.content,
  };

  // PRIORITY 0: If slide needs an image, create an image variant of the natural template
  const needsImage = slide.needsImage || slide.needs_image;
  const currentType = slide.slideType || slide.slide_type;

  // PRIORITY 1: Use AI-recommended slide type if provided (Gamma-style)
  const aiRecommendedType = slide.slideType || slide.slide_type;
  const slideBulletPoints = slide.bulletPoints || slide.bullet_points || [];
  let selectedTemplate = null;
  if (aiRecommendedType && templateMap[aiRecommendedType]) {
    const candidate = templateMap[aiRecommendedType];

    // Comparison template needs leftItems/rightItems which outlines don't produce
    // Fall back to versus (which uses bullet_points) for comparison slides with bullets
    if (aiRecommendedType === 'comparison' && slideBulletPoints.length >= 2 && !slide.leftItems) {
      selectedTemplate = templateMap['versus'] || templateMap['icon-bullets'] || candidate;
    } else {
      // If slide has bullet points but template has no bullet_points placeholder, pick a better template
      const hasBulletPlaceholder = candidate?.layout?.elements?.some(el =>
        el.placeholder === 'bullet_points' || el.placeholder === 'bulletPoints'
      );
      if (slideBulletPoints.length >= 2 && !hasBulletPlaceholder) {
        selectedTemplate = templateMap['icon-bullets'] || templateMap['bullets'] || candidate;
      } else {
        selectedTemplate = candidate;
      }
    }
  }

  // If no AI recommendation, use pattern detection
  if (!selectedTemplate) {
    const pattern = detectLayoutPattern(slide, index, totalSlides);

    // Check for closing slide keywords on last slide (only if no bullet content)
    const slideBullets = slide.bulletPoints || slide.bullet_points || [];
    if (index === totalSlides - 1 && slideBullets.length === 0 && (pattern === 'content' || pattern === 'section' || pattern === 'big-statement')) {
      const closingKeywords = ['thank', 'question', 'contact', 'q&a'];
      const titleLower = (slide.title || '').toLowerCase();
      if (closingKeywords.some(kw => titleLower.includes(kw))) {
        selectedTemplate = theme.templates.closing || theme.templates.title;
      }
    }
    if (!selectedTemplate) {
      selectedTemplate = templateMap[pattern] || theme.templates.content;
    }
  }

  // Apply image variant if needed (dynamically transforms the template)
  if (needsImage && selectedTemplate && !NO_IMAGE_VARIANT.has(selectedTemplate.id)) {
    console.log(`Slide ${index + 1}: Using image variant of "${selectedTemplate.id}"`);
    return createImageVariant(selectedTemplate, theme.colorScheme);
  }
  // If needs image but template doesn't support variants, fall back to text-with-image
  if (needsImage && selectedTemplate && NO_IMAGE_VARIANT.has(selectedTemplate.id) && templateMap['text-with-image']) {
    console.log(`Slide ${index + 1}: Falling back to text-with-image (${selectedTemplate.id} doesn't support image variant)`);
    return templateMap['text-with-image'];
  }

  return selectedTemplate;
};

/**
 * Get slide pattern - uses AI-recommended type if available, otherwise pattern detection
 */
export const getSlidePattern = (slide, index, totalSlides) => {
  // Return AI-recommended type if available
  const aiType = slide.slideType || slide.slide_type;
  if (aiType) return aiType;

  // Fallback to pattern detection
  return detectLayoutPattern(slide, index, totalSlides);
};

/**
 * Prepare slide data for rendering based on pattern
 * Extracts stats, splits bullets, etc. as needed
 */
export const prepareSlideData = (slide, pattern) => {
  const prepared = { ...slide };

  switch (pattern) {
    case 'stat-highlight':
      // Extract the main statistic from content
      prepared.stats = extractStatistics(slide);
      if (prepared.stats.length > 0) {
        prepared.stat = prepared.stats[0].value;
      }
      break;

    case 'timeline':
      // Parse bullet points into timeline items with years
      prepared.timelineItems = parseTimelineItems(slide.bulletPoints || slide.bullet_points || []);
      break;

    case 'bullets-two-col':
      // Split bullets into left and right columns
      const split = splitBulletsForColumns(slide.bulletPoints || slide.bullet_points || []);
      prepared.bullet_points_left = split.left;
      prepared.bullet_points_right = split.right;
      break;

    default:
      break;
  }

  return prepared;
};

/**
 * Apply brand colors to a theme's color scheme
 */
export const applyBrandColors = (colorScheme, brandGuidelines) => {
  if (!brandGuidelines || !brandGuidelines.colors || brandGuidelines.colors.length === 0) {
    return colorScheme;
  }

  const colors = brandGuidelines.colors;
  const updatedScheme = { ...colorScheme };

  if (colors[0]) {
    updatedScheme.primary = colors[0].hex || colors[0];
  }
  if (colors[1]) {
    updatedScheme.secondary = colors[1].hex || colors[1];
  }
  if (colors[2]) {
    updatedScheme.accent = colors[2].hex || colors[2];
  }

  return updatedScheme;
};

/**
 * Get all available template types for a theme
 */
export const getTemplateTypes = (theme) => {
  return Object.keys(theme.templates);
};

export default themes;
